// Tool-agnostic conversion instrumentation.
//
// Events are pushed to window.dataLayer so a Google Tag Manager container or
// GA4 (which reads the same dataLayer) can consume them with zero code changes
// — you only need to drop the container/measurement snippet into index.html.
//
// No cookies or PII are written here, so these pushes are privacy-safe on their
// own. When you add the actual analytics loader (which DOES set cookies), gate
// it behind cookie consent — see src/utils/consent.js.
import { GOOGLE_ADS_CONVERSIONS, INTEGRATIONS } from './integrations.js';

// Maps our internal event names to Meta Pixel standard events. Events not
// listed here are forwarded to Meta as custom events (fbq trackCustom).
const META_STANDARD_EVENTS = {
  page_view: 'PageView',
  demo_request: 'Lead',
  newsletter_signup: 'Subscribe',
  install_intent: 'InitiateCheckout',
  modal_open: 'ViewContent',
};

// The Meta Pixel auto-fires one PageView on load; this guards against the SPA's
// initial-mount page_view double-counting it. Flipped true on the first
// page_view that reaches the pixel-forwarding branch.
let metaInitialPageViewSeen = false;

export function track(event, params = {}) {
  if (typeof window === 'undefined') return;
  // GTM reads dataLayer directly.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
  // GA4-direct (gtag, no GTM) needs an explicit event call to receive it.
  //
  // Deliberately skipped when GTM is configured. The dataLayer push above already
  // reaches GA4 through the container, and window.gtag now exists even in
  // GTM mode — the Google Ads tag defines it (see loadGoogleAds). Forwarding here
  // as well would push a second, gtag-shaped entry for the same event, which the
  // container also reads, double-firing every GA4 event tag.
  if (!INTEGRATIONS.gtmId && typeof window.gtag === 'function') {
    window.gtag('event', event, params);
  }

  // Google Ads conversion action. Fires only for events with a configured
  // send_to label, so events without one cost nothing and misconfiguration
  // cannot report a phantom conversion against the wrong action.
  const sendTo = GOOGLE_ADS_CONVERSIONS[event];
  if (sendTo && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', { send_to: sendTo, ...params });
  }
  // Meta Pixel — only fires once fbq is loaded (i.e. after marketing consent).
  if (typeof window.fbq === 'function') {
    // The pixel's own init already fires one PageView for the page it loads on
    // (see loadMetaPixel in integrations.js), so skip the FIRST page_view here
    // to avoid double-counting. Subsequent page_views are real SPA route
    // changes and must be forwarded.
    if (event === 'page_view' && !metaInitialPageViewSeen) {
      metaInitialPageViewSeen = true;
      return;
    }
    const metaEvent = META_STANDARD_EVENTS[event];
    if (metaEvent) window.fbq('track', metaEvent, params);
    else window.fbq('trackCustom', event, params);
  }
}

export function trackPageView(path) {
  const page_path =
    path || (typeof location !== 'undefined' ? location.pathname : '');
  track('page_view', { page_path });
}
